import { useCallback, useRef } from 'react';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import type { ViewToken } from 'react-native';
import { ColorValues, Flex, List } from '@banhala/ui-kit';
import type { MarketCarouselEntity, MarketCarouselProps } from './types';
import { ThreeImageHeader } from './components/ThreeImageHeader';
import { MarketCard } from './components/MarketCard';
import { FavoriteButton } from './components/FavoriteButton';
import { bridge, isNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';

const INFO_HEIGHT = 68;
const CARD_BORDER_RADIUS = 4;

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

const keyExtractor = (_item: MarketCarouselEntity, index: number) =>
  index.toString();

interface ThreeImageCardProps extends MarketCarouselEntity {
  cardWidth: number;
}

const ThreeImageCard = ({
  item,
  logging,
  cardWidth,
}: ThreeImageCardProps) => {
  const { sno, profile, name, description, goods_images, favorite } = item;
  const { openDeeplink } = useOpenDeeplink();

  const handlePressCard = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_MARKET',
      analytics: logging.analytics,
      ably_only_log: { ...logging.ably_only_log, event_name: 'click' },
    });
    openDeeplink(`ably://markets/${sno}/`);
  }, [openDeeplink, sno, logging]);

  return (
    <View style={styles.cardPadding}>
      <View style={[styles.cardShadow, { borderRadius: CARD_BORDER_RADIUS }]}>
        <Flex
          width={cardWidth}
          borderRadius={CARD_BORDER_RADIUS}
          overflow="hidden"
          onPress={handlePressCard}
        >
          <ThreeImageHeader images={goods_images} cardWidth={cardWidth} />

          <MarketCard
            sno={sno}
            profile={profile}
            name={name}
            description={description}
            logging={logging}
            profileSize={40}
            nameType="subtitle3"
            descriptionType="body5"
            descriptionColor="content_secondary"
            height={INFO_HEIGHT}
            paddingHorizontal={14}
            paddingVertical={12}
            pressable={false}
            showChevron={false}
            rightElement={
              <FavoriteButton
                sno={sno}
                isFavorited={favorite?.is_favorited ?? false}
                logging={logging}
              />
            }
          />
        </Flex>
      </View>
    </View>
  );
};

export const ThreeImageMarketCarousel = ({ entity }: MarketCarouselProps) => {
  const { width: screenWidth } = useWindowDimensions();
  const cardWidth = Math.round(screenWidth * 0.86);
  const displayedRef = useRef(new Set<number>());

  const onViewableItemsChanged = useCallback(
    ({
      viewableItems,
    }: {
      viewableItems: ViewToken<MarketCarouselEntity>[];
    }) => {
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index === null || !isViewable || isNil(item)) return;
        if (displayedRef.current.has(index)) return;
        displayedRef.current.add(index);
        bridge.logAnalyticsEvent({
          name: 'DISPLAY_MARKET',
          analytics: {
            ...item.logging.analytics,
            IS_FAVORITED: item.item.favorite?.is_favorited ? 1 : 0,
          },
          ably_only_log: item.logging.ably_only_log,
        });
      });
    },
    [],
  );

  const renderItem = useCallback(
    ({ item }: { item: MarketCarouselEntity }) => (
      <ThreeImageCard
        item={item.item}
        logging={item.logging}
        cardWidth={cardWidth}
      />
    ),
    [cardWidth],
  );

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 12,
    paddingBottom: 20,
    gap: 12,
  },
  cardPadding: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  cardShadow: {
    backgroundColor: ColorValues.white,
    shadowColor: ColorValues.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
});
